import React, { useState, useRef, useEffect } from "react";
import "./FloatingButton.css";
import { createSession, processAudio, processText } from "../../../utils/api";
import { useLocalization } from "../../../context/LocalizationContext";
import { AI_COMMANDS } from "../../../constants";
import WhiteMicIcon from "../../../assets/icons/AI/whiteMic.svg";
import SpeakingIcon from "../../../assets/icons/AI/speaking.png";
import NotSpeakingIcon from "../../../assets/icons/AI/notSpeaking.png";

const FloatingButton = ({
  icon,
  label,
  position = "bottom-right",
  children,
  ariaLabel,
  userData,
  onClick
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSession, setHasSession] = useState(false);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("arabic");
  const [selectedGender, setSelectedGender] = useState(() => {
    return userData?.gender?.toLowerCase() || "male";
  });
  const sessionIdRef = useRef(null);
  const audioRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const isMountedRef = useRef(true);
  const playPromiseRef = useRef(null);
  const { language, t } = useLocalization();

  useEffect(() => {
    isMountedRef.current = true;
    
    // Cleanup audio on unmount
    return () => {
      isMountedRef.current = false;
      
      // Stop audio immediately
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0; // Reset to beginning
        if (audioRef.current.src && audioRef.current.src.startsWith('blob:')) {
          URL.revokeObjectURL(audioRef.current.src);
        }
        audioRef.current = null;
      }
      
      // Cancel any pending play promise
      if (playPromiseRef.current) {
        playPromiseRef.current.catch(() => {}); // Suppress any errors
        playPromiseRef.current = null;
      }
      
      // Stop recording if still recording
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  const handleToggle = () => {
    // If custom onClick is provided, use it instead
    if (onClick) {
      onClick();
    } else {
      setIsOpen(!isOpen);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const playAudio = (audioData) => {
    // Check if component is still mounted before proceeding
    if (!isMountedRef.current) {
      return;
    }

    try {
      // Clean up previous audio if exists
      if (audioRef.current) {
        audioRef.current.pause();
        if (audioRef.current.src && audioRef.current.src.startsWith('blob:')) {
          URL.revokeObjectURL(audioRef.current.src);
        }
        audioRef.current = null;
      }

      // Cancel any pending play promise
      if (playPromiseRef.current) {
        playPromiseRef.current.catch(() => {});
        playPromiseRef.current = null;
      }

      // Create new audio element
      const audio = new Audio();
      audioRef.current = audio;

      // Handle different audio response formats
      if (typeof audioData === 'string') {
        // If it's a URL or base64 data URL
        if (audioData.startsWith('data:audio') || audioData.startsWith('http')) {
          audio.src = audioData;
        } else {
          // Assume it's a base64 string without prefix
          audio.src = `data:audio/mpeg;base64,${audioData}`;
        }
      } else if (audioData instanceof Blob) {
        // If it's a Blob, create object URL
        audio.src = URL.createObjectURL(audioData);
      } else if (audioData?.audio_url) {
        // If response has audio_url property
        audio.src = audioData.audio_url;
      } else if (audioData?.audio) {
        // If response has audio property (base64 or URL)
        audio.src = audioData.audio.startsWith('data:') || audioData.audio.startsWith('http')
          ? audioData.audio
          : `data:audio/mpeg;base64,${audioData.audio}`;
      }

      // Set up audio event handlers - check if mounted before setting state
      audio.onplay = () => {
        if (isMountedRef.current) {
          setIsAudioPlaying(true);
        } else if (audioRef.current) {
          audioRef.current.pause();
        }
      };

      audio.onended = () => {
        if (isMountedRef.current) {
          setIsAudioPlaying(false);
        }
        if (audio.src.startsWith('blob:')) {
          URL.revokeObjectURL(audio.src);
        }
      };

      audio.onpause = () => {
        if (isMountedRef.current) {
          setIsAudioPlaying(false);
        }
      };

      audio.onerror = () => {
        if (isMountedRef.current) {
          setIsAudioPlaying(false);
        }
      };

      // Play the audio and store the promise
      const playPromise = audio.play();
      playPromiseRef.current = playPromise;
      
      playPromise
        .then(() => {
          playPromiseRef.current = null;
        })
        .catch(error => {
          playPromiseRef.current = null;
          if (isMountedRef.current) {
            console.error("Error playing audio:", error);
            setIsAudioPlaying(false);
          }
        });
    } catch (error) {
      console.error("Error setting up audio playback:", error);
      if (isMountedRef.current) {
        setIsAudioPlaying(false);
      }
    }
  };

  const handleStart = async () => {
    try {
      setIsLoading(true);
      const gender = selectedGender; // Use selected gender from state
      const lang = selectedLanguage; // Use selected language instead of context language

      const response = await createSession(lang, gender);
      sessionIdRef.current = response.session_id;
      setHasSession(true);
      console.log("Session created, sessionId:", sessionIdRef.current);

      // Play the returned audio
      if (response.audio || response.audio_url || response.audio_data) {
        playAudio(response.audio || response.audio_url || response.audio_data);
      } else if (response.audioBlob) {
        // If response has audioBlob, create object URL
        playAudio(response.audioBlob);
      } else if (response) {
        // If the entire response is audio data
        playAudio(response);
      }
    } catch (error) {
      console.error("Error creating session:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        // Stop all tracks to release microphone
        stream.getTracks().forEach(track => track.stop());

        // Create audio blob
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });

        // Process the audio
        if (sessionIdRef.current && audioBlob.size > 0) {
          try {
            setIsLoading(true);
            const response = await processAudio(sessionIdRef.current, audioBlob);

            // Play the response audio if available
            if (response.audio || response.audio_url || response.audio_data) {
              playAudio(response.audio || response.audio_url || response.audio_data);
            } else if (response.audioBlob) {
              playAudio(response.audioBlob);
            } else if (response) {
              playAudio(response);
            }
          } catch (error) {
            console.error("Error processing audio:", error);
          } finally {
            setIsLoading(false);
          }
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error("Error starting recording:", error);
      alert("Failed to access microphone. Please check your permissions.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const handleTalkToAI = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const handleTextCommand = async (command) => {
    if (!sessionIdRef.current) {
      console.error("No active session");
      return;
    }

    try {
      setIsLoading(true);
      const response = await processText(sessionIdRef.current, command);

      // Play the response audio if available
      if (response.audio || response.audio_url || response.audio_data) {
        playAudio(response.audio || response.audio_url || response.audio_data);
      } else if (response.audioBlob) {
        playAudio(response.audioBlob);
      } else if (response) {
        playAudio(response);
      }
    } catch (error) {
      console.error("Error processing text command:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {!isOpen && (
        <button
          className={`floating-btn floating-btn-${position}`}
          onClick={handleToggle}
          aria-label={ariaLabel || label}
        >
          {icon && <img src={icon} alt="" className="floating-btn-icon" />}
          {label && <span className="floating-btn-label">{label}</span>}
        </button>
      )}

      {isOpen && (
        <div className={`floating-window floating-window-${position}`}>
          <button
            className="floating-window-close"
            onClick={handleClose}
            aria-label="Close"
          >
            <span className="close-icon">×</span>
          </button>
          <div className="floating-window-content">
            {children}
            {!hasSession && (
              <>
                <div className="floating-window-language-selector">
                  <label className="floating-window-language-label">{t("aiAssistant.chooseLanguage")}</label>
                  <div className="floating-window-language-options">
                    <button
                      className={`floating-window-language-btn ${selectedLanguage === "arabic" ? "active" : ""}`}
                      onClick={() => setSelectedLanguage("arabic")}
                      disabled={isLoading}
                    >
                      {language === "en" ? "Arabic" : "العربية"}
                    </button>
                    <button
                      className={`floating-window-language-btn ${selectedLanguage === "english" ? "active" : ""}`}
                      onClick={() => setSelectedLanguage("english")}
                      disabled={isLoading}
                    >
                      {t("language.english")}
                    </button>
                  </div>
                </div>
                <div className="floating-window-gender-selector">
                  <label className="floating-window-language-label">{t("aiAssistant.chooseGender")}</label>
                  <div className="floating-window-language-options">
                    <button
                      className={`floating-window-language-btn ${selectedGender === "male" ? "active" : ""}`}
                      onClick={() => setSelectedGender("male")}
                      disabled={isLoading}
                    >
                      {t("male")}
                    </button>
                    <button
                      className={`floating-window-language-btn ${selectedGender === "female" ? "active" : ""}`}
                      onClick={() => setSelectedGender("female")}
                      disabled={isLoading}
                    >
                      {t("female")}
                    </button>
                  </div>
                </div>
              </>
            )}
            {!hasSession ? (
              <button
                className="floating-window-start-btn"
                onClick={handleStart}
                disabled={isLoading}
              >
                {isLoading ? t("aiAssistant.starting") : t("aiAssistant.start")}
              </button>
            ) : (
              <>
                <div className="floating-window-voice-indicator">
                  <img
                    src={isAudioPlaying ? SpeakingIcon : NotSpeakingIcon}
                    alt={isAudioPlaying ? "Speaking" : "Not Speaking"}
                    className={`voice-indicator-icon ${isAudioPlaying ? 'speaking' : ''}`}
                  />
                </div>
                {isRecording && (
                  <div className="listening-text">
                    {t("aiAssistant.listening")}
                  </div>
                )}
                {!isRecording && (
                  <div className="floating-window-command-buttons">
                    <button
                      className="floating-window-command-btn"
                      onClick={() => handleTextCommand(AI_COMMANDS.REPEAT_INSTRUCTIONS)}
                      disabled={isLoading || isAudioPlaying}
                    >
                      {t("aiAssistant.repeatInstructions")}
                    </button>
                    <button
                      className="floating-window-command-btn"
                      onClick={() => handleTextCommand(AI_COMMANDS.SKIP)}
                      disabled={isLoading || isAudioPlaying}
                    >
                      {t("aiAssistant.skip")}
                    </button>
                  </div>
                )}
                <div className={`floating-window-ask-btn-wrapper ${isRecording ? 'recording' : ''} ${isAudioPlaying ? 'playing' : ''}`}>
                  <button
                    className={`floating-window-ask-btn ${isAudioPlaying ? 'playing' : ''}`}
                    onClick={handleTalkToAI}
                    disabled={isLoading || isAudioPlaying}
                  >
                    {!isRecording && <img src={WhiteMicIcon} alt="Microphone" className="ask-btn-icon" />}
                    <span>
                      {isRecording ? t("aiAssistant.clickAgainToStop") : t("aiAssistant.talkToAI")}
                    </span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default FloatingButton;
